import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useDeliveries } from '@/hooks/useDeliveries'
import { useAvailability, useUpdateAvailability } from '@/hooks/useAvailability'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { formatCurrency, formatRelativeTime } from '@/lib/utils'
import { 
  Package, 
  Truck, 
  DollarSign, 
  Clock, 
  MapPin,
  ChevronRight,
  Loader2
} from 'lucide-react'

export default function DashboardPage() {
  const { user } = useAuth()
  const { data: deliveries, isLoading: deliveriesLoading } = useDeliveries()
  const { data: availability } = useAvailability()
  const updateAvailability = useUpdateAvailability()

  const activeDeliveries = deliveries?.filter(
    (d) => ['assigned', 'pickup_ready', 'picked_up', 'in_transit'].includes(d.status)
  ) || []
  
  const todayDeliveries = deliveries?.filter(
    (d) => new Date(d.createdAt).toDateString() === new Date().toDateString()
  ) || []

  const todayEarnings = todayDeliveries
    .filter((d) => d.status === 'delivered')
    .reduce((sum, d) => sum + d.deliveryFee, 0)

  const handleToggleOnline = () => {
    if (availability) {
      updateAvailability.mutate({ isOnline: !availability.isOnline })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Hello, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-muted-foreground">
            {availability?.isOnline ? "You're online and ready to deliver" : "You're currently offline"}
          </p>
        </div>
        
        {/* Online Toggle */}
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${availability?.isOnline ? 'text-success' : 'text-muted-foreground'}`}>
            {availability?.isOnline ? 'Online' : 'Offline'}
          </span>
          <Switch
            checked={availability?.isOnline ?? false}
            onCheckedChange={handleToggleOnline}
            disabled={updateAvailability.isPending}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-xl">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeDeliveries.length}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-success/10 rounded-xl">
                <DollarSign className="w-6 h-6 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(todayEarnings)}</p>
                <p className="text-sm text-muted-foreground">Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-secondary/10 rounded-xl">
                <Truck className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {todayDeliveries.filter((d) => d.status === 'delivered').length}
                </p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-accent/10 rounded-xl">
                <Clock className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">{todayDeliveries.length}</p>
                <p className="text-sm text-muted-foreground">Total Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Deliveries */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Active Deliveries</CardTitle>
          <Link to="/deliveries">
            <Button variant="ghost" size="sm">
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {deliveriesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : activeDeliveries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No active deliveries</p>
              <p className="text-sm">New orders will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeDeliveries.slice(0, 3).map((delivery) => (
                <Link
                  key={delivery.orderId}
                  to={`/deliveries/${delivery.orderId}`}
                  className="block"
                >
                  <div className="flex items-center gap-4 p-4 rounded-xl border hover:bg-muted/50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">#{delivery.orderNumber}</span>
                        <StatusBadge status={delivery.status} />
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{delivery.dropoff.address}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(delivery.deliveryFee)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatRelativeTime(delivery.createdAt)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
    assigned: { label: 'Assigned', className: 'bg-blue-100 text-blue-800' },
    pickup_ready: { label: 'Ready', className: 'bg-green-100 text-green-800' },
    picked_up: { label: 'Picked Up', className: 'bg-purple-100 text-purple-800' },
    in_transit: { label: 'In Transit', className: 'bg-indigo-100 text-indigo-800' },
    delivered: { label: 'Delivered', className: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
  }

  const { label, className } = config[status] || { label: status, className: 'bg-gray-100 text-gray-800' }

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  )
}
